/**
 * Density custody — the second half of Appearance (WEB TIER A · WT7).
 *
 * The owner's own phrase for the control is "Text size / density", and
 * `settings.html` draws it as three stops with the register row redrawn at
 * each one, "so the effect is visible rather than described". This module is
 * the storage-and-attribute half of that; the CSS half is the
 * `--crf-density-*` block in `styles/tokens.css`, keyed off the
 * `data-density` attribute this file writes on `<html>`.
 *
 * ===========================================================================
 * WHY IT IS SHAPED EXACTLY LIKE `theme.tsx` AND NOT LIKE A CONTEXT
 * ===========================================================================
 * Theme's resolution has TWO inputs (an explicit choice and the OS), so it
 * needs a provider to keep the OS listener alive. Density has ONE: what the
 * reader chose, or the default. Nothing observes it but CSS — the attribute on
 * `<html>` IS the subscription, and every consumer is a `var()` — so a React
 * context here would be a second source of truth for a value the document
 * already carries. Plain module, no provider, no hook.
 *
 * The FIRST paint is handled by the inline script in `index.html`, the same
 * pattern and for the same reason as the theme: a React effect runs after the
 * first paint, so a reader on `large` would see the rows resize under them.
 * The storage key is shared between the two places and must stay in sync —
 * it is `crf.density` in both.
 *
 * ===========================================================================
 * THE STOPS ARE MEASURED FIGURES, NOT A MULTIPLIER
 * ===========================================================================
 * `settings.html` measures its own study after mount and asserts a leading
 * cell of 14 / 14 / 16 px: type moves at the THIRD stop only, and it moves by
 * exactly ONE STEP OF THE TYPE RAMP (14→16, 13→14, 12→13). Its stated reason
 * is the reason this file does not carry a scale factor: "a 1.125 multiplier
 * would produce 14.625px, which is not a size this ramp has, and off-ramp
 * sizes are exactly what the last check caught". The TYPE stops are untouched
 * by the ruling below; only the ROW stop moved.
 *
 * ┌── OQ-1 · RESOLVED 2026-08-18 · OWNER RULING, RECORDED VERBATIM ──────────┐
 * │ The question was: the drawn study moves a SINGLE-LINE ledger row         │
 * │ 36→44→52, but the register that shipped (WT3, the WORKLIST) draws a      │
 * │ TWO-LINE 52px row, and the mapping of the three stops onto THAT row was  │
 * │ not drawn anywhere in the approved set. Plan §6 recorded it as OQ-1, an  │
 * │ OWNER ruling, because the build rule is that a derived mapping is        │
 * │ flagged rather than invented.                                            │
 * │                                                                          │
 * │ Given three options, the owner answered "A". Verbatim:                   │
 * │                                                                          │
 * │   "(a) Row height only — 44 / 52 / 60px, type moves only at 'Large       │
 * │    text'. Truest to the approved mockup, smallest change."               │
 * │                                                                          │
 * │ …and recommended "plus applying it to every register rather than just    │
 * │ the worklist".                                                           │
 * │                                                                          │
 * │ SO, AS BUILT: `--crf-density-row` is 44 / 52 / 60. The type stops are    │
 * │ unchanged — third stop only, one ramp step. The consumer is no longer    │
 * │ the Appearance preview alone: the `crf-register-row` utility in          │
 * │ `styles/app.css` carries the token, and EVERY register row, every        │
 * │ register skeleton row, and the preview itself wear that one class. 52px  │
 * │ is the height the worklist already shipped at, so a reader who never     │
 * │ opens Appearance sees the flagship register redraw at the pixel it       │
 * │ already had.                                                             │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

export type Density = 'compact' | 'comfortable' | 'large';

/** Shared with the pre-paint script in `index.html`. Keep both in sync. */
export const DENSITY_STORAGE_KEY = 'crf.density';

/** What a reader who has never chosen gets. The mockup's "The default". */
export const DEFAULT_DENSITY: Density = 'comfortable';

export const DENSITIES: readonly Density[] = ['compact', 'comfortable', 'large'];

export function isDensity(value: unknown): value is Density {
  return value === 'compact' || value === 'comfortable' || value === 'large';
}

/**
 * The stored choice, or `null` when nothing is stored (or storage is blocked —
 * a private-mode browser that throws on `getItem` is a reader on the default,
 * not an error state).
 */
export function readStoredDensity(): Density | null {
  try {
    const value = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return isDensity(value) ? value : null;
  } catch {
    return null;
  }
}

/** The density in force: the stored choice, else the default. */
export function currentDensity(): Density {
  return readStoredDensity() ?? DEFAULT_DENSITY;
}

/**
 * Writes the attribute the tokens key off. Kept separate from `setDensity` so
 * the value can be applied without being stored — which is what happens when
 * storage is blocked and the choice still has to take effect for the session.
 */
export function applyDensity(density: Density): void {
  document.documentElement.setAttribute('data-density', density);
}

/**
 * The reader's choice: persisted, then applied. Storage failure is swallowed
 * for the same reason `theme.tsx` swallows it — the choice still applies for
 * this session, it just will not survive a reload, and refusing to change the
 * rows because a browser refused a write would be the worse answer.
 */
export function setDensity(density: Density): void {
  try {
    window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
  } catch {
    // Applied below regardless; see the comment above.
  }
  applyDensity(density);
}

/**
 * The three stops, with the words `settings.html` gives them. Data rather than
 * markup so the page and its test read the same list, and so a fourth stop is
 * a one-line change in one place.
 */
export interface DensityOption {
  value: Density;
  label: string;
  detail: string;
}

export const DENSITY_OPTIONS: readonly DensityOption[] = [
  { value: 'compact', label: 'Compact', detail: 'Same text, tighter rows' },
  { value: 'comfortable', label: 'Comfortable', detail: 'The default' },
  { value: 'large', label: 'Large text', detail: 'One ramp step larger, and taller rows' },
];
